"use client";

import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export interface FilterCountSummaryProps {
  totalCount: number | null;
  matchingCount: number | null;
  matchingCountIsPartial?: boolean;
  hasActiveFilters: boolean;
  isLoading?: boolean;
  className?: string;
}

function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return value.toLocaleString();
}

function CountLoader() {
  return (
    <Loader2
      className="ml-2 inline-block size-3.5 animate-spin align-text-bottom text-muted-foreground"
      aria-hidden="true"
    />
  );
}

export function FilterCountSummary({
  totalCount,
  matchingCount,
  matchingCountIsPartial = false,
  hasActiveFilters,
  isLoading = false,
  className,
}: FilterCountSummaryProps) {
  // Prefer spinner while counts are in flight — never flash a stale "0+" mid-filter.
  const matchingLabel = isLoading ? (
    <CountLoader />
  ) : matchingCountIsPartial ? (
    `${formatCount(matchingCount)}+`
  ) : (
    formatCount(matchingCount)
  );

  return (
    <div
      className={cn(
        "shrink-0 rounded-md border bg-muted/40 px-3 py-2 text-right text-xs tabular-nums",
        className,
      )}
      aria-live="polite"
    >
      <div className="text-muted-foreground">
        Total in table
        {isLoading && totalCount === null ? (
          <CountLoader />
        ) : (
          <span className="ml-2 font-semibold text-foreground">{formatCount(totalCount)}</span>
        )}
      </div>
      <div className="mt-0.5 text-muted-foreground">
        {hasActiveFilters ? "After filters" : "Showing"}
        {typeof matchingLabel === "string" ? (
          <span className="ml-2 font-semibold text-foreground">{matchingLabel}</span>
        ) : (
          matchingLabel
        )}
      </div>
    </div>
  );
}
